<template>
  <VaCard>
    <VaCardTitle>
      <h3 class="mb-3 font-bold text-lg" style="text-transform: none">Marcas torniquete</h3>
    </VaCardTitle>
    <VaCardContent class="h-full scrollable-table">
      <table class="mt-4">
        <tbody>
          <template v-for="(item, index) in displayList" :key="index">
            <!-- Day divider -->
            <tr v-if="item.type === 'divider'">
              <td colspan="2" class="py-2">
                <div class="day-divider">
                  <span class="day-divider__label">{{ item.label }}</span>
                </div>
              </td>
            </tr>
            <!-- Single mark -->
            <VaTimelineItem
              v-else-if="item.type === 'marca'"
              :date="
                item.eventDate.toLocaleDateString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              Usuario <b>{{ item.userName }}</b> realizó una marca en el sistema.
            </VaTimelineItem>
            <!-- Accumulated mark -->
            <VaTimelineItem
              v-else-if="item.type === 'marca-acum'"
              :date="
                item.eventDate.toLocaleDateString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              "
            >
              Usuario <b>{{ item.userName }}</b> marcó para <b>{{ item.count }}</b> pasajeros.
            </VaTimelineItem>
          </template>
        </tbody>
      </table>
      <div class="flex justify-center gap-2 mt-4">
        <VaButton v-if="visibleCount < showList.length" preset="secondary" size="small" @click="visibleCount += 20">
          Mostrar más
        </VaButton>
        <VaButton preset="secondary" size="small" :loading="isLoadingMore" @click="loadPreviousDay">
          Ver mes anterior
        </VaButton>
      </div>
    </VaCardContent>
  </VaCard>
</template>
<script setup lang="ts">
import { onMounted, ref, Ref, watch, computed } from 'vue'
import VaTimelineItem from '../../../components/va-timeline-item.vue'
import dataStorageManager, { BucketDefaultIDs } from '../../../managers/data/DataStorageManager'
import { ITripulante } from '../../../services/api-services/TripulantesServices'
import { IMarca, MarcasController } from '../../../services/api-services/MarcasServices'
import { IArmador } from '../../../services/api-services/ArmadoresServices'
import { IUser } from '../../../services/api-services/UsersServices'
import { rutify } from '../../../services/apiUtils'

// El servidor retorna record_time sin indicador de zona horaria (UTC).
// Sin 'Z', V8 lo interpreta como hora local → desfase fijo. Se normaliza añadiendo 'Z'.
function parseServerDate(dateStr: string): Date {
  if (dateStr && !dateStr.endsWith('Z') && !dateStr.includes('+') && !/T\d{2}:\d{2}:\d{2}-\d{2}/.test(dateStr)) {
    return new Date(dateStr + 'Z')
  }
  return new Date(dateStr)
}

function formatIdAsRut(idUser: number): string {
  if (idUser < 1_000_000 || idUser > 99_999_999) return `ID: ${idUser}`
  let sum = 0
  let multiplier = 2
  let n = idUser
  while (n > 0) {
    sum += (n % 10) * multiplier
    n = Math.floor(n / 10)
    multiplier = multiplier < 7 ? multiplier + 1 : 2
  }
  const rem = 11 - (sum % 11)
  const dv = rem === 11 ? '0' : rem === 10 ? 'K' : rem.toString()
  const dots = idUser.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `RUT: ${dots}-${dv}`
}

const marcasController = new MarcasController()

const usuariosList: Ref<IUser[]> = ref([])
const armadoresList: Ref<IArmador[]> = ref([])
const tripulantesList: Ref<ITripulante[]> = ref([])

const loadedDays = ref(30)
const isLoadingMore = ref(false)

type MarcaEvent = {
  type: 'marca' | 'marca-acum'
  userName: string
  eventDate: Date
  count: number
}

type DividerItem = {
  type: 'divider'
  label: string
  eventDate: Date
}

const fullList: Ref<MarcaEvent[]> = ref([])
const showList: Ref<MarcaEvent[]> = ref([])

function getRangeStart(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - loadedDays.value)
  return d
}

function getRangeEnd(): Date {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d
}

async function loadMarksForRange() {
  await marcasController.loadData(getRangeStart(), getRangeEnd(), 1000 * 30)
}

async function loadPreviousDay() {
  isLoadingMore.value = true
  loadedDays.value += 30
  await loadMarksForRange()
  isLoadingMore.value = false
}

onMounted(() => {
  dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, (usuarios: IUser[]) => {
    usuariosList.value = usuarios
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, (armadores: IArmador[]) => {
    armadoresList.value = armadores
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, (tripulantes: ITripulante[]) => {
    tripulantesList.value = tripulantes
  })
  loadMarksForRange()
})

watch([usuariosList, armadoresList, tripulantesList, marcasController.marcasList], () => {
  const marcaTimeRange = 1000 * 60 * 5
  const rawMarcas = marcasController.getMarcasData().filter((m) => m.id_user != null && m.record_time != null)
  const grouped = [...rawMarcas]
    .sort((a, b) => parseServerDate(b.record_time).getTime() - parseServerDate(a.record_time).getTime())
    .reduce((acc: { marca: IMarca; count: number; lastHour: Date }[], marca: IMarca) => {
      const marcaTime = parseServerDate(marca.record_time)
      const existing = acc.find(
        (item) =>
          item.marca.id_user === marca.id_user &&
          Math.abs(item.lastHour.getTime() - marcaTime.getTime()) < marcaTimeRange,
      )
      if (existing) {
        existing.count += 1
        if (marcaTime > existing.lastHour) existing.lastHour = marcaTime
      } else {
        acc.push({ marca, count: 1, lastHour: marcaTime })
      }
      return acc
    }, [])

  const rutNum = (rut: string) => rutify(rut.toString()).split('-')[0]

  fullList.value = grouped.map(({ marca, count, lastHour }) => {
    const idStr = marca.id_user.toString()
    const user = usuariosList.value.find((u) => rutNum(u.rut) === idStr) ?? null
    const armador = armadoresList.value.find((a) => rutNum(a.rut_representante) === idStr) ?? null
    const tripulante = tripulantesList.value.find((t) => rutNum(t.rut) === idStr) ?? null

    const userName = user?.nombre_completo
      ? `${user.nombre_completo} (${user.rut})`
      : armador?.nombre_representante
        ? `${armador.nombre_representante} (${armador.rut_representante})`
        : tripulante?.nombre_completo
          ? `${tripulante.nombre_completo} (${tripulante.rut})`
          : formatIdAsRut(marca.id_user)

    return {
      type: count > 1 ? 'marca-acum' : 'marca',
      userName,
      eventDate: lastHour,
      count,
    } as MarcaEvent
  })
})

const visibleCount = ref(20)

watch(fullList, () => {
  showList.value = [...fullList.value]
    .sort((a, b) => b.eventDate.getTime() - a.eventDate.getTime())
    .filter((v) => v.eventDate >= getRangeStart())
  visibleCount.value = 20
})

// Auto-load going further back until we have at least 15 marks (max 90 days back)
watch(showList, async () => {
  if (showList.value.length < 15 && !isLoadingMore.value && loadedDays.value < 90) {
    await loadPreviousDay()
  }
})

const visibleList = computed(() => showList.value.slice(0, visibleCount.value))

const displayList = computed<(MarcaEvent | DividerItem)[]>(() => {
  const result: (MarcaEvent | DividerItem)[] = []
  let lastDayKey = ''
  for (const item of visibleList.value) {
    const d = item.eventDate
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    if (dayKey !== lastDayKey) {
      const isToday =
        d.getDate() === new Date().getDate() &&
        d.getMonth() === new Date().getMonth() &&
        d.getFullYear() === new Date().getFullYear()
      result.push({
        type: 'divider',
        label: isToday ? 'Hoy' : d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' }),
        eventDate: d,
      })
      lastDayKey = dayKey
    }
    result.push(item)
  }
  return result
})
</script>
<style scoped>
.scrollable-table {
  overflow-y: auto;
  height: 100%;
  max-height: 520px;
}

b {
  color: #4774bd;
}

.day-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
}

.day-divider::before,
.day-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--va-background-border, #e0e0e0);
}

.day-divider__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--va-secondary);
  opacity: 0.7;
  white-space: nowrap;
  padding: 0 4px;
}
</style>
